import { useCallback, useEffect, useState } from "react";
import { AdminButton, AdminCard, AdminErrorBanner, AdminFormField, AdminInfoRow, AdminLoadingState, AdminPageLayout, AdminTextInput } from "@/components/admin";
import SoundTable from "./components/SoundTable";
import InfoCard from "./components/InfoCard";
import {
  fetchAdminSoundConfidenceDistribution,
  fetchAdminSoundLabelStatistics,
  fetchAdminSoundPolicy,
  fetchAdminSoundStatisticsSummary,
  fetchDashboardSummary,
  fetchSoundAnalysisResultPage,
  simulateAdminSoundThreshold,
} from "../../../lib/api";
import type {
  AdminSoundConfidenceBin,
  AdminSoundLabelStatistics,
  AdminSoundStatisticsSummary,
  AdminSoundThresholdPolicy,
  AdminSoundThresholdSimulationResult,
  SoundAnalysisV2ListItem,
} from "../../../lib/api";

const SoundAnalysisPage = () => {
  const [records, setRecords] = useState<SoundAnalysisV2ListItem[]>([]);
  const [summary, setSummary] = useState<AdminSoundStatisticsSummary | null>(null);
  const [labelStats, setLabelStats] = useState<AdminSoundLabelStatistics[]>([]);
  const [confidenceBins, setConfidenceBins] = useState<AdminSoundConfidenceBin[]>([]);
  const [policy, setPolicy] = useState<AdminSoundThresholdPolicy | null>(null);
  const [simulation, setSimulation] = useState<AdminSoundThresholdSimulationResult | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [policyVersion, setPolicyVersion] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPageSize] = useState(20);
  const [totalRecordCount, setTotalRecordCount] = useState(0);
  const [totalRecordPages, setTotalRecordPages] = useState(1);
  const [noneThreshold, setNoneThreshold] = useState("0.15");
  const [contextThreshold, setContextThreshold] = useState("0.30");
  const [attentionThreshold, setAttentionThreshold] = useState("0.45");
  const [highThreshold, setHighThreshold] = useState("0.65");
  const [loading, setLoading] = useState(false);
  const [loadingOps, setLoadingOps] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [opsErrorMessage, setOpsErrorMessage] = useState<string | null>(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const nextPage = await fetchSoundAnalysisResultPage({
        page: currentPage - 1,
        size: recordPageSize,
      });
      setRecords(nextPage.items);
      setTotalRecordCount(nextPage.totalElements);
      setTotalRecordPages(Math.max(1, nextPage.totalPages));
    } catch (error) {
      setRecords([]);
      setTotalRecordCount(0);
      setTotalRecordPages(1);
      setErrorMessage(error instanceof Error ? error.message : "소리 분석 결과를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordPageSize]);

  const loadOperationsFor = useCallback(async (
    requestedFromDate: string,
    requestedToDate: string,
    requestedPolicyVersion: string,
  ) => {
    if (!requestedFromDate || !requestedToDate) {
      setOpsErrorMessage("운영 통계 조회 기간을 입력하세요. 입력한 업무일자는 변환 없이 API에 전달됩니다.");
      return;
    }
    const normalizedPolicyVersion = requestedPolicyVersion.trim();
    setLoadingOps(true);
    setOpsErrorMessage(null);
    try {
      const params = {
        fromDate: requestedFromDate,
        toDate: requestedToDate,
        activationPolicyVersion: normalizedPolicyVersion || undefined,
      };
      const [nextSummary, nextLabels, nextBins] = await Promise.all([
        fetchAdminSoundStatisticsSummary(params),
        fetchAdminSoundLabelStatistics(params),
        fetchAdminSoundConfidenceDistribution(params),
      ]);
      setSummary(nextSummary);
      setLabelStats(nextLabels);
      setConfidenceBins(nextBins);
      if (normalizedPolicyVersion) {
        setPolicy(await fetchAdminSoundPolicy(normalizedPolicyVersion));
      } else {
        setPolicy(null);
      }
    } catch (error) {
      setSummary(null);
      setLabelStats([]);
      setConfidenceBins([]);
      setPolicy(null);
      setOpsErrorMessage(error instanceof Error ? error.message : "과거 소리 v2 통계를 불러오지 못했습니다.");
    } finally {
      setLoadingOps(false);
    }
  }, []);

  const loadOperations = useCallback(
    () => loadOperationsFor(fromDate, toDate, policyVersion),
    [fromDate, loadOperationsFor, policyVersion, toDate],
  );

  useEffect(() => {
    void loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    void fetchDashboardSummary()
      .then((dashboard) => {
        setFromDate(dashboard.rollingSevenDayStartDate);
        setToDate(dashboard.businessDate);
        void loadOperationsFor(dashboard.rollingSevenDayStartDate, dashboard.businessDate, "");
      })
      .catch((error: unknown) => {
        setOpsErrorMessage(error instanceof Error ? error.message : "API 업무일자를 불러오지 못했습니다.");
      });
  }, [loadOperationsFor]);

  const handleSimulation = useCallback(async () => {
    if (!fromDate || !toDate) {
      setOpsErrorMessage("임계값 시뮬레이션 기간을 입력하세요.");
      return;
    }
    setSimulating(true);
    setOpsErrorMessage(null);
    try {
      const result = await simulateAdminSoundThreshold({
        fromDate,
        toDate,
        activationPolicyVersion: policyVersion.trim() || undefined,
        noneThreshold: Number(noneThreshold),
        contextThreshold: Number(contextThreshold),
        attentionThreshold: Number(attentionThreshold),
        highThreshold: Number(highThreshold),
      });
      setSimulation(result);
    } catch (error) {
      setSimulation(null);
      setOpsErrorMessage(error instanceof Error ? error.message : "임계값 시뮬레이션에 실패했습니다.");
    } finally {
      setSimulating(false);
    }
  }, [attentionThreshold, contextThreshold, fromDate, highThreshold, noneThreshold, policyVersion, toDate]);

  return (
    <AdminPageLayout
      title="과거 소리 v2 이벤트"
      subtitle="YAMNet 후보·모델 버전·v2 정책 기반의 과거 분석 이력을 조회합니다."
      actions={(
        <>
          <AdminButton variant="secondary" size="sm" icon={loadingOps ? "ri-refresh-line animate-spin" : "ri-bar-chart-box-line"} onClick={() => void loadOperations()} disabled={loadingOps}>
            운영 통계
          </AdminButton>
          <AdminButton variant="primary" size="sm" icon={loading ? "ri-refresh-line animate-spin" : "ri-refresh-line"} onClick={loadRecords} disabled={loading}>
            새로고침
          </AdminButton>
        </>
      )}
    >
      <div className="mb-4">
        <AdminErrorBanner message={errorMessage} />
        <AdminErrorBanner message={opsErrorMessage} />
      </div>

      <div className="mb-4 rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white">
            <i className="ri-sound-module-line" />
          </div>
          <div>
            <p className="text-sm font-bold text-violet-900">과거 Sound v2 조회 기준</p>
            <p className="mt-1 text-xs leading-5 text-violet-700">
              이 화면은 `/api/v2/admin/sound-analysis-events` 기반 과거 운영 이력입니다. 최신 EAFR v3 운영 화면으로 해석하지 않으며 후보 Top10·메트릭·튜닝 이력 확인 용도로만 사용합니다.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <AdminCard className="p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-slate-800">운영 통계</h3>
            <div className="flex items-center gap-1">
              <AdminTextInput type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} aria-label="통계 시작 업무일자" className="w-32" />
              <span className="text-xs text-slate-400">~</span>
              <AdminTextInput type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} aria-label="통계 종료 업무일자" className="w-32" />
            </div>
          </div>
          {loadingOps ? (
            <AdminLoadingState message="소리 통계 조회 중" />
          ) : (
            <div className="space-y-1">
              <AdminInfoRow dense bordered label="운영 이벤트" value={summary?.operationalEventCount ?? "-"} />
              <AdminInfoRow dense bordered label="진단 이벤트" value={summary?.diagnosticEventCount ?? "-"} />
              <AdminInfoRow dense bordered label="저신뢰 진단" value={summary?.lowConfidenceDiagnosticCount ?? "-"} />
              <AdminInfoRow dense bordered label="미매핑 진단" value={summary?.unmappedDiagnosticCount ?? "-"} />
              <AdminInfoRow dense bordered label="후보 관측" value={summary?.uniqueDisplayCandidateObservationCount ?? "-"} />
            </div>
          )}
        </AdminCard>

        <AdminCard className="p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-slate-800">정책 조회</h3>
            <AdminTextInput
              value={policyVersion}
              onChange={(event) => setPolicyVersion(event.target.value)}
              className="w-36"
              placeholder="정확한 정책 버전(선택)"
            />
          </div>
          <div className="space-y-1">
            <AdminInfoRow dense bordered label="조회 범위" value={policyVersion.trim() || "전체 정책"} />
            <AdminInfoRow dense bordered label="정책 상태" value={policy?.policyStatusCode ?? "-"} />
            <AdminInfoRow dense bordered label="후보 floor" value={policy?.candidateFloor ?? "-"} />
            <AdminInfoRow dense bordered label="상대 비율" value={policy?.candidateRelativeRatio ?? "-"} />
            <AdminInfoRow dense bordered label="정책 rule" value={policy ? `${policy.rules.length}개` : "-"} />
            <AdminInfoRow dense bordered label="근거" value={policy?.evidenceReference ?? "-"} />
          </div>
        </AdminCard>

        <AdminCard className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800">임계값 시뮬레이션</h3>
            <AdminButton variant="secondary" size="sm" onClick={handleSimulation} disabled={simulating} icon={simulating ? "ri-loader-4-line animate-spin" : "ri-play-circle-line"}>
              실행
            </AdminButton>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              ["NONE", noneThreshold, setNoneThreshold],
              ["CTX", contextThreshold, setContextThreshold],
              ["ATTN", attentionThreshold, setAttentionThreshold],
              ["HIGH", highThreshold, setHighThreshold],
            ].map(([label, value, setter]) => (
              <AdminFormField key={String(label)} label={String(label)}>
                <AdminTextInput value={String(value)} onChange={(event) => (setter as (next: string) => void)(event.target.value)} />
              </AdminFormField>
            ))}
          </div>
          <div className="mt-3 space-y-1">
            <AdminInfoRow dense bordered label="현재 eligible" value={simulation?.currentEligibleCount ?? "-"} />
            <AdminInfoRow dense bordered label="제안 eligible" value={simulation?.proposedEligibleCount ?? "-"} />
            <AdminInfoRow dense bordered label="변화량" value={simulation?.eligibleDelta ?? "-"} />
          </div>
        </AdminCard>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <AdminCard className="p-4">
          <h3 className="mb-3 text-sm font-bold text-slate-800">라벨별 후보 관측 Top 8</h3>
          <div className="space-y-2">
            {labelStats.slice(0, 8).map((item) => (
              <div key={`${item.soundGroupCode}-${item.displayLabelCode}`} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-700">{item.displayLabel}</p>
                  <p className="text-xs text-slate-400">{item.soundGroupCode} · {item.riskLevelCode} · {item.operating ? "운영" : "비운영"}</p>
                </div>
                <span className="text-sm font-black text-slate-800">{item.candidateObservationCount}</span>
              </div>
            ))}
            {labelStats.length === 0 && <p className="text-sm text-slate-500">라벨 통계가 없습니다.</p>}
          </div>
        </AdminCard>

        <AdminCard className="p-4">
          <h3 className="mb-3 text-sm font-bold text-slate-800">신뢰도 분포</h3>
          <div className="space-y-2">
            {confidenceBins.map((bin) => {
              const max = Math.max(...confidenceBins.map((item) => item.candidateObservationCount), 1);
              const width = Math.max(4, Math.round((bin.candidateObservationCount / max) * 100));
              return (
                <div key={`${bin.scoreFromInclusive}-${bin.scoreToExclusive}`} className="grid grid-cols-[6rem_minmax(0,1fr)_3.5rem] items-center gap-2 text-xs">
                  <span className="font-semibold text-slate-500">{bin.scoreFromInclusive}~{bin.scoreToExclusive}</span>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-violet-500" style={{ width: `${width}%` }} />
                  </div>
                  <span className="text-right font-bold text-slate-700">{bin.candidateObservationCount}</span>
                </div>
              );
            })}
            {confidenceBins.length === 0 && <p className="text-sm text-slate-500">신뢰도 분포가 없습니다.</p>}
          </div>
        </AdminCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3">
          <SoundTable
            records={records}
            searchQuery={searchQuery}
            loading={loading}
            currentPage={currentPage}
            totalPages={totalRecordPages}
            totalCount={totalRecordCount}
            pageSize={recordPageSize}
            onPageChange={setCurrentPage}
            onSearchChange={setSearchQuery}
            onRefresh={loadRecords}
          />
        </div>

        <div className="xl:col-span-1">
          <InfoCard />
        </div>
      </div>
    </AdminPageLayout>
  );
};

export default SoundAnalysisPage;
